import {
  type DependencyList,
  type RefObject,
  useCallback,
  useLayoutEffect,
  useState,
} from 'react';

interface Size {
  width: number;
  height: number;
  clientWidth: number;
  clientHeight: number;
  scrollWidth: number;
  scrollHeight: number;
}

export const useMeasure = <T extends HTMLElement>(
  ref: RefObject<T>,
  deps: DependencyList = []
): Size => {
  const [size, setSize] = useState<Size>({
    width: 0,
    height: 0,
    clientWidth: 0,
    clientHeight: 0,
    scrollWidth: 0,
    scrollHeight: 0,
  });

  const updateSize = useCallback(() => {
    if (!ref.current) return;

    const { width, height } = ref.current.getBoundingClientRect();
    const {
      clientWidth = 0,
      clientHeight = 0,
      scrollWidth = 0,
      scrollHeight = 0,
    } = ref.current;

    setSize((prevSize) => {
      // Only update state if size actually changed
      if (
        prevSize.width !== width ||
        prevSize.height !== height ||
        prevSize.clientWidth !== clientWidth ||
        prevSize.clientHeight !== clientHeight ||
        prevSize.scrollWidth !== scrollWidth ||
        prevSize.scrollHeight !== scrollHeight
      ) {
        return {
          width,
          height,
          clientWidth,
          clientHeight,
          scrollWidth,
          scrollHeight,
        };
      }
      return prevSize;
    });
  }, [ref]);

  useLayoutEffect(() => {
    if (!ref.current) return;

    // Initial measurement to ensure immediate value on first render
    updateSize();

    if (!window.ResizeObserver) {
      // Fallback for environments without ResizeObserver
      window.addEventListener('resize', updateSize);
      return () => {
        window.removeEventListener('resize', updateSize);
      };
    }

    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(ref.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [ref.current, updateSize, ...deps]);

  return size;
};
